import { useCallback, useEffect, useMemo, useState } from 'react';
import { calculateInvoice } from '../lib/gst';
import { local } from '../lib/storage';

const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : Math.random().toString(36).slice(2) + Date.now().toString(36);

export const emptyLineItem = () => ({
  id: uid(),
  description: '',
  hsn: '',
  qty: 1,
  unit: 'Nos',
  rate: 0,
  discount: 0,
  gstRate: 18,
});

export const defaultBusiness = {
  name: '',
  gstin: '',
  address: '',
  state: '',
  stateName: '',
  phone: '',
  email: '',
  logo: '',
  sealSignature: '',
  bankDetails: { bankName: '', accountNo: '', ifsc: '', upi: '' },
};

export const defaultCustomer = {
  name: '', gstin: '', address: '', state: '', stateName: '', phone: '', email: '',
};

export function useInvoice() {
  const draft = local.getDraft();
  const [business, setBusiness] = useState(() => draft?.business || local.getBusiness() || defaultBusiness);
  const [customer, setCustomer] = useState(() => draft?.customer || defaultCustomer);
  const [lineItems, setLineItems] = useState(() => draft?.lineItems?.length ? draft.lineItems.map((l) => ({ ...l, id: uid() })) : [emptyLineItem()]);
  const [notes, setNotes] = useState(() => draft?.notes || '');
  const [bjKey, setBjKey] = useState(() => draft?.bj_key || draft?.invoice_key || uid());
  const [number, setNumber] = useState(() => draft?.number || local.nextInvoiceNumber());
  const [date, setDate] = useState(() => draft?.date || new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => draft?.dueDate || '');
  const [paymentTerms, setPaymentTerms] = useState(() => draft?.paymentTerms || 'custom');
  const [customPaymentTerms, setCustomPaymentTerms] = useState(() => draft?.customPaymentTerms || '');
  const [template, setTemplate] = useState(() => draft?.template || 'modern');

  useEffect(() => {
    if (draft) local.clearDraft();
  }, []);

  const { lineItems: enrichedLines, taxSummary } = useMemo(
    () => calculateInvoice(lineItems, business.state, customer.state),
    [lineItems, business.state, customer.state]
  );

  const addLineItem = useCallback(() => setLineItems((prev) => [...prev, emptyLineItem()]), []);
  const addMultipleLineItems = useCallback((n = 5) => setLineItems((prev) => [...prev, ...Array.from({ length: n }, emptyLineItem)]), []);
  const removeLineItem = useCallback((id) => setLineItems((prev) => prev.filter((l) => l.id !== id)), []);
  const removeLineItems = useCallback((ids) => setLineItems((prev) => prev.filter((l) => !ids.has(l.id))), []);
  const duplicateLineItem = useCallback((id) => {
    setLineItems((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copy = { ...prev[idx], id: uid() };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }, []);
  const updateLineItem = useCallback((id, patch) => {
    setLineItems((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }, []);

  const invoice = {
    bj_key: bjKey,
    number, date, dueDate,
    business,
    customer,
    lineItems: enrichedLines,
    taxSummary,
    notes,
    paymentTerms,
    customPaymentTerms,
    template,
  };

  const reset = useCallback(() => {
    setCustomer(defaultCustomer);
    setLineItems([emptyLineItem()]);
    setNotes('');
    setDate(new Date().toISOString().slice(0, 10));
    setDueDate('');
    setPaymentTerms('custom');
    setCustomPaymentTerms('');
    setBjKey(uid());
  }, []);

  return {
    business, setBusiness,
    customer, setCustomer,
    lineItems, setLineItems, addLineItem, addMultipleLineItems, removeLineItem, removeLineItems, duplicateLineItem, updateLineItem,
    notes, setNotes,
    number, setNumber,
    date, setDate,
    dueDate, setDueDate,
    paymentTerms, setPaymentTerms,
    customPaymentTerms, setCustomPaymentTerms,
    template, setTemplate,
    enrichedLines, taxSummary,
    invoice,
    reset,
  };
}
